import { useState } from "react";
import { Link } from "react-router";
import { ListIllustration } from "vega-illustrations";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { type EmptyCardProps } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { useExportFilename } from "../../hooks/useExportFilename";
import { useTableExport } from "../../hooks/useTableExport";

import { TrackAnalyticsToolbar } from "./components/TrackAnalyticsToolbar";
import { useTrackAnalyticsColumns } from "./hooks/useTrackAnalyticsColumns";

type TrackAnalyticsTableProps = {
  data: DealTrackingLimitedPartnerBreakdown[];
  isLoading?: boolean;
  dealId: string;
};

export const TrackAnalyticsTable = ({
  data,
  isLoading = false,
  dealId,
}: TrackAnalyticsTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { columnDefs, renderColumnToggle, exportColumns } =
    useTrackAnalyticsColumns();

  const stageOptions = Array.from(
    new Set(
      data
        .map((row) => row.stage?.trim())
        .filter((stage): stage is string => isTruthy(stage)),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map((stage) => ({ value: stage, label: stage }));

  const tierOptions = Array.from(
    new Set(data.map((row) => row.tier).filter(isTruthy)),
  )
    .sort((a, b) => Number(a) - Number(b))
    .map((tier) => ({ value: tier, label: tier }));

  const filtersConfig = {
    filters: [
      {
        id: "stage",
        type: FilterType.Dropdown,
        label: m.coInvestments_tracking_stage(),
        props: {
          placeholder: m.coInvestments_tracking_stage_placeholder({
            value: m.common_all(),
          }),
          renderValue: (label: string) =>
            m.coInvestments_tracking_stage_withValue({ label }),
          options: stageOptions,
          className: "w-56",
        },
      },
      {
        id: "tier",
        type: FilterType.Dropdown,
        label: m.coInvestments_tracking_tier(),
        props: {
          placeholder: m.coInvestments_tracking_tier_placeholder({
            value: m.common_all(),
          }),
          renderValue: (label: string) =>
            m.coInvestments_tracking_tier_withValue({ label }),
          options: tierOptions,
          className: "w-24",
        },
      },
    ],
    maxShownFilters: 2,
    showResetButton: true,
    noMarginBottom: true,
  } as const satisfies TableFiltersConfig;

  const { filterState, hasActiveFilters, RenderFilters } = useTableFilters({
    id: TableFilterId.CoInvestmentsAnalytics,
    config: filtersConfig,
    persistInSession: false,
    syncWithURL: false,
  });

  const stageFilter = filterState.stage?.value;
  const tierFilter = filterState.tier?.value;
  const search = searchQuery.trim().toLowerCase();
  const isFiltered = hasActiveFilters || isTruthy(searchQuery);

  const filteredData = isFiltered
    ? data.filter((row) => {
        if (search && !row.limitedPartnerName.toLowerCase().includes(search))
          return false;

        if (stageFilter && row.stage?.trim() !== stageFilter) return false;

        if (tierFilter && row.tier !== tierFilter) return false;

        return true;
      })
    : data;

  const exportFilename = useExportFilename(dealId, "tracking");
  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: filteredData,
    columns: exportColumns,
    filename: exportFilename,
  });

  const gridEmptyState: EmptyCardProps = isFiltered
    ? { title: m.coInvestments_tracking_noFilterResults() }
    : {
        title: m.coInvestments_tracking_noDataTitle(),
        illustration: <ListIllustration />,
        description: (
          <>
            {m.trackingtable_emptyState_uploadPrefix()}{" "}
            <Link
              className="text-text/secondary underline"
              data-testid="go-to-prospects-button"
              to={CoInvestmentFullPaths.CoInvestmentShare.replace(
                ":id",
                dealId,
              )}
            >
              {m.trackingtable_emptyState_prospectsLinkText()}
            </Link>{" "}
            {m.trackingtable_emptyState_uploadSuffix()}
          </>
        ),
      };

  return (
    <>
      <h2 className="heading-5 text-text/primary mb-4">
        {m.coInvestments_tracking_limitedPartners()}
      </h2>
      <div className="flex flex-col gap-4 mb-4">
        <TrackAnalyticsToolbar
          isExporting={isExporting}
          onExportCsv={exportToCsv}
          onExportExcel={exportToExcel}
          onSearchQueryChange={setSearchQuery}
          renderColumnToggle={renderColumnToggle}
          renderFilters={RenderFilters}
          searchQuery={searchQuery}
        />
      </div>
      <div data-testid="track-analytics-table">
        <DataGrid<DealTrackingLimitedPartnerBreakdown>
          columnDefs={columnDefs}
          emptyState={gridEmptyState}
          getRowId={(params) => params.data.dealOpportunityId}
          loading={isLoading}
          rowData={filteredData}
        />
      </div>
    </>
  );
};
